import { useMemo, useState } from 'react';
import type {
  BylawCategory,
  ComplainantRelationship,
  FilePriority,
  IntakeMethod,
  OwnerType,
  ParcelInputDto,
  PriorityReason,
  PropertyType,
} from '@munifocus/contracts-gov';
import {
  PAN_PATTERN,
  PID_PATTERN,
  opensComplainantSection,
  opensInternalSourceSection,
  requiresPriorityReason,
} from '@munifocus/contracts-gov';
import { useIntakeMethods } from '../../lib/taxonomies-api';

/**
 * Every field a file is made of — the complaint that opened it and the property
 * it concerns, in one shape.
 *
 * One shape because there is one form. Creating a file and correcting one ask
 * for exactly the same information; splitting them into separate screens with
 * separate field sets is how a create form and an edit form drift apart until
 * they no longer describe the same record.
 */
export interface FileFormValues {
  // §A — how the matter reached the municipality.
  intakeMethod: IntakeMethod | '';
  intakeMethodOther: string;
  // §B — the reporter. Only collected for methods that open the section.
  anonymous: boolean;
  complainantName: string;
  complainantEmail: string;
  complainantPhone: string;
  // The complainant's own address, in the four boxes the complaint report
  // prints. Distinct from `address`, which is the property complained about.
  complainantAddress: string;
  complainantCity: string;
  complainantProvince: string;
  complainantPostalCode: string;
  complainantRelationship: ComplainantRelationship | '';
  complainantRelationshipOther: string;
  complainantLocale: 'en' | 'fr';
  // §C — internal source, for methods that open that section instead.
  referringPerson: string;
  internalContext: string;
  // The matter itself.
  address: string;
  /**
   * The local government whose enforcement this is — the registry anchor the
   * document assembler reads letterhead from. Empty means unanchored, which is
   * the correct answer for an LSD or unincorporated area and a gap anywhere
   * else; generated documents then fall back to `taxingAuthority`.
   */
  localGovernmentId: string;
  /** A row from the enforced-law registry, or '' for "not recorded". */
  enforcedLawId: string;
  /**
   * `app_user.id` of the officer working the file, or empty for the queue.
   * Only meaningful alongside an anchor — assignment authority is checked
   * against the registry, and an unanchored file has nothing to check against.
   */
  assignedOfficerId: string;
  propertyType: PropertyType | '';
  propertyTypeOther: string;
  bylawCategory: BylawCategory;
  bylawCategoryOther: string;
  priority: FilePriority;
  priorityReason: PriorityReason | '';
  priorityReasonOther: string;
  description: string;
  // Tombstone (Stage 1 spec §3–§6). One PAN → many PIDs.
  pan: string;
  county: string;
  parcelRows: ParcelInputDto[];
  ownerName: string;
  ownerEmail: string;
  // Officer-entered: the PAN record carries no telephone for anyone.
  ownerPhone: string;
  ownerLocale: 'en' | 'fr' | '';
  ownerType: OwnerType | '';
  ownerTypeOther: string;
  mailingAddress: string;
  harmonizationStatus: string;
  ownerUnknown: boolean;
  // The occupant of the premises (LGA s. 131(1), s. 132) — a party in their own
  // right, never inferred from the owner.
  occupantName: string;
  occupantPhone: string;
  occupantAddress: string;
  occupantCity: string;
  occupantProvince: string;
  occupantPostalCode: string;
  propertyDescription: string;
  taxingAuthority: string;
  // Carried, not shown on the short form — but never dropped, because a record
  // supplied them and losing them silently is worse than not reading them.
  postalCode: string;
  taxClass: string;
  neighbourhood: string;
}

export const EMPTY_FILE_FORM: FileFormValues = {
  intakeMethod: '',
  intakeMethodOther: '',
  anonymous: false,
  complainantName: '',
  complainantEmail: '',
  complainantPhone: '',
  complainantAddress: '',
  complainantCity: '',
  complainantProvince: '',
  complainantPostalCode: '',
  complainantRelationship: '',
  complainantRelationshipOther: '',
  complainantLocale: 'en',
  referringPerson: '',
  internalContext: '',
  address: '',
  localGovernmentId: '',
  enforcedLawId: '',
  assignedOfficerId: '',
  propertyType: '',
  propertyTypeOther: '',
  // Neither default asserts anything the officer has to undo: D&U is the
  // workflow this product implements, and routine is the resting priority.
  bylawCategory: 'dangerous_unsightly',
  bylawCategoryOther: '',
  priority: 'routine',
  priorityReason: '',
  priorityReasonOther: '',
  description: '',
  pan: '',
  county: '',
  parcelRows: [{ pid: '', isPrimary: true }],
  ownerName: '',
  ownerEmail: '',
  ownerPhone: '',
  ownerLocale: '',
  ownerType: '',
  ownerTypeOther: '',
  mailingAddress: '',
  harmonizationStatus: '',
  ownerUnknown: false,
  occupantName: '',
  occupantPhone: '',
  occupantAddress: '',
  occupantCity: '',
  occupantProvince: '',
  occupantPostalCode: '',
  propertyDescription: '',
  taxingAuthority: '',
  postalCode: '',
  taxClass: '',
  neighbourhood: '',
};

export interface FileForm {
  values: FileFormValues;
  set: <K extends keyof FileFormValues>(key: K, value: FileFormValues[K]) => void;
  /** Fill a field only if it is still empty — what an SNB record is allowed to do. */
  fillIfBlank: (key: keyof FileFormValues, value: string | null | undefined) => void;
  /** Replace the whole form, e.g. when the record being edited finally loads. */
  reset: (values: FileFormValues) => void;
  setPidAt: (index: number, pid: string) => void;
  setParcelFieldAt: (index: number, key: keyof ParcelInputDto, value: string) => void;
  addPid: () => void;
  removePid: (index: number) => void;
  setParcelRows: (rows: ParcelInputDto[]) => void;

  // ── Derived ───────────────────────────────────────────────────────────────
  /** Trimmed, non-empty PIDs with the first marked primary. */
  parcels: ParcelInputDto[];
  /** Which of the two conditional sections the chosen method opens. */
  showComplainant: boolean;
  showInternalSource: boolean;
  councilReferral: boolean;
  showPriorityReason: boolean;
  /** A harmonized PAN spans amalgamated parcels — expect more than one PID. */
  harmonized: boolean;
  /** The Stage 1 gate: what a file cannot be worked without. */
  canSubmit: boolean;
  /** Whether anything differs from what the form opened with. */
  dirty: boolean;
}

/**
 * The file form's state, shared by the create and edit screens.
 *
 * `initial` is what the form opened with — an empty form when creating, the
 * stored record when editing. `dirty` is measured against it either way, so
 * "would leaving lose anything?" has one answer and one implementation.
 *
 * `baseline` overrides what `dirty` compares against, for the one case where the
 * two differ: intake restored from a recovered session draft opens *with* the
 * officer's typed values, but those values are still unsaved work. Measured
 * against `initial` the form would call itself clean and let them navigate away
 * from it without a word.
 */
export function useFileForm(
  initial: FileFormValues = EMPTY_FILE_FORM,
  baselineOverride?: FileFormValues,
): FileForm {
  const [values, setValues] = useState<FileFormValues>(initial);
  const [baseline, setBaseline] = useState<FileFormValues>(baselineOverride ?? initial);
  const intakeMethods = useIntakeMethods();

  const set = <K extends keyof FileFormValues>(key: K, value: FileFormValues[K]) =>
    setValues((prev) => ({ ...prev, [key]: value }));

  const fillIfBlank = (key: keyof FileFormValues, value: string | null | undefined) =>
    setValues((prev) => {
      const current = prev[key];
      if (typeof current !== 'string' || current !== '') return prev;
      return { ...prev, [key]: value ?? '' };
    });

  const reset = (next: FileFormValues) => {
    setValues(next);
    setBaseline(next);
  };

  const setParcelRows = (rows: ParcelInputDto[]) => set('parcelRows', rows);
  const setPidAt = (index: number, pid: string) =>
    setValues((prev) => ({
      ...prev,
      parcelRows: prev.parcelRows.map((p, i) => (i === index ? { ...p, pid } : p)),
    }));
  const setParcelFieldAt = (index: number, key: keyof ParcelInputDto, value: string) =>
    setValues((prev) => ({
      ...prev,
      parcelRows: prev.parcelRows.map((p, i) => (i === index ? { ...p, [key]: value } : p)),
    }));
  const addPid = () =>
    setValues((prev) => ({
      ...prev,
      parcelRows: [...prev.parcelRows, { pid: '', isPrimary: false }],
    }));
  const removePid = (index: number) =>
    setValues((prev) => ({
      ...prev,
      parcelRows: prev.parcelRows.filter((_, i) => i !== index),
    }));

  const parcels = useMemo<ParcelInputDto[]>(
    () =>
      values.parcelRows
        .map((p) => ({ ...p, pid: p.pid.trim() }))
        .filter((p) => p.pid.length > 0)
        .map((p, idx) => ({ ...p, isPrimary: idx === 0 })),
    [values.parcelRows],
  );

  const method = values.intakeMethod === '' ? null : values.intakeMethod;
  const methodOptions = intakeMethods.data ?? [];
  const showComplainant = method !== null && opensComplainantSection(method, methodOptions);
  const showInternalSource = method !== null && opensInternalSourceSection(method, methodOptions);
  const councilReferral =
    method !== null && (methodOptions.find((o) => o.key === method)?.isCouncilReferral ?? false);
  const showPriorityReason = requiresPriorityReason(values.priority);

  // PID + owner identity are the Stage 1 assignment gate per the docs; the file
  // can't be worked until the address and a substantive description exist.
  // Property type joins them as mandatory: it decides whether Stage 2 renders
  // the Entry & Access Module, and defaulting it would assert an entry position
  // on the officer's behalf. Intake method joins them for the same reason — it
  // decides which reporter details are collected at all.
  const canSubmit =
    values.address.trim().length > 0 &&
    values.description.trim().length > 0 &&
    values.propertyType !== '' &&
    values.intakeMethod !== '' &&
    // The local government joins them: it is the tenant a file belongs to, and
    // an unanchored file would be visible to nobody but a Munifocus operator —
    // including the officer who just opened it. `LocalGovernmentPicker` fills it
    // in without asking when the officer holds a single appointment, so this
    // only ever stops somebody appointed at several who has not yet chosen.
    values.localGovernmentId !== '' &&
    // §F makes the reason mandatory above routine, so an unjustified elevation
    // can't be submitted — the API rejects it too, but not after the officer
    // has filled in the rest of the form.
    (!showPriorityReason || values.priorityReason !== '');

  const dirty = useMemo(
    () => JSON.stringify(values) !== JSON.stringify(baseline),
    [values, baseline],
  );

  return {
    values,
    set,
    fillIfBlank,
    reset,
    setPidAt,
    setParcelFieldAt,
    addPid,
    removePid,
    setParcelRows,
    parcels,
    showComplainant,
    showInternalSource,
    councilReferral,
    showPriorityReason,
    harmonized: values.harmonizationStatus.trim().length > 0,
    canSubmit,
    dirty,
  };
}

/** The fields the Stage 1 gate checks, in the order they appear on screen. */
export type FileFormGateField =
  | 'intakeMethod'
  | 'address'
  | 'localGovernmentId'
  | 'propertyType'
  | 'priorityReason'
  | 'description'
  | 'pan'
  | 'pids';

/**
 * Why the form cannot be submitted yet, field by field — the same rules as
 * `canSubmit`, stated. Fed to `useSubmitValidation` so the submit button stays
 * live and answers a premature press at the field that needs attention.
 */
export function fileFormGateMessages(
  form: FileForm,
  t: (key: string) => string,
): Record<FileFormGateField, string | null> {
  const { values } = form;
  const required = t('common.validation.required');
  const choose = t('common.validation.choose');
  return {
    intakeMethod: values.intakeMethod !== '' ? null : choose,
    address: values.address.trim() ? null : required,
    localGovernmentId: values.localGovernmentId !== '' ? null : choose,
    propertyType: values.propertyType !== '' ? null : choose,
    priorityReason: !form.showPriorityReason || values.priorityReason !== '' ? null : choose,
    description: values.description.trim() ? null : required,
    // Optional, but when given they are the SNB formats: an 8-digit PID and a
    // digits-only PAN. The API refuses anything else, so say so here first.
    pan:
      !values.pan.trim() || PAN_PATTERN.test(values.pan.trim()) ? null : t('gov.file.panInvalid'),
    pids: values.parcelRows.every((r) => !r.pid.trim() || PID_PATTERN.test(r.pid.trim()))
      ? null
      : t('gov.file.pidInvalid'),
  };
}
